import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import ApplyLeaveTab from "../components/ApplyLeaveTab";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ProfileTab({ user, currentUser, form, onEditLeave }) {
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem("token");

  const fetchProfileData = async () => {
    setLoading(true);
    try {
      let userDetails = null;

      if (form == "admin") {
        userDetails = user;
      } else {
        userDetails = null;
      }

      // ✅ Only fetch /users/details if no specific user is passed
      if (!userDetails) {
        const usersResponse = await axios.get(`${API}/users/details`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        userDetails = usersResponse.data.data.user;
      }

      setProfileData(userDetails);

      // ✅ Fetch all leaves
      const leavesResponse = await axios.get(`${API}/leaves`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (leavesResponse.data.success) {
        const allLeaves = leavesResponse.data.data.leaves;
        // ✅ Filter by correct user
        const userLeaves = allLeaves.filter(
          (leave) => leave.user_id === userDetails.id,
        );
        const sortedByIdDesc = userLeaves.sort((a, b) => b.id - a.id);

        setLeaves(sortedByIdDesc);
      }
    } catch (error) {
      console.error("Error fetching profile data:", error);
      toast.error("Failed to load profile data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfileData();
  }, []);

  const calculateLeaveSummary = () => {
    const approved = leaves.filter((l) => l.status === "approved").length;
    const pending = leaves.filter((l) => l.status === "pending").length;
    const rejected = leaves.filter((l) => l.status === "rejected").length;
    const totalDaysApproved = leaves
      .filter((l) => l.status === "approved")
      .reduce((sum, l) => sum + l.duration, 0);

    return { approved, pending, rejected, totalDaysApproved };
  };

  if (loading) {
    return (
      <div className="empty-state" data-testid="profile-loading">
        <p>Loading profile...</p>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="empty-state" data-testid="profile-not-found">
        <p>Profile not found</p>
      </div>
    );
  }

  const summary = calculateLeaveSummary();
  const joiningDate = new Date(profileData.joining_date).toLocaleDateString(
    "en-US",
    {
      year: "numeric",
      month: "long",
      day: "numeric",
    },
  );

  const handleViewDetails = () => {
    // navigate or open modal
    console.log("View Details clicked!", profileData);
    navigate(`/profile/${profileData.id}`); // optional if using react-router
  };

  const deleteLeave = async (leaveId) => {
    try {
      const response = await axios.post(
        `${API}/leaves/delete`,
        { leave_id: leaveId }, // JSON body
        {
          headers: {
            Authorization: `Bearer ${token}`, // keep as you have it
            "Content-Type": "application/json",
          },
        },
      );

      const data = response.data; // axios already parses JSON

      if (data.success) {
        console.log("Deleted successfully:", leaveId);
        // update UI
        setLeaves((prev) => prev.filter((l) => l.id !== leaveId));
      } else {
        console.error("Delete failed:", data.message || data);
        // optionally show toast to user
      }
    } catch (error) {
      console.error(
        "Delete error:",
        error?.response?.data || error.message || error,
      );
      // optionally show toast to user
    }
  };

  const makeUnpaid = async (leaveId) => {
    try {
      const response = await axios.patch(`${API}/leaves/${leaveId}/unpaid`, {}, {
        headers: {
          Authorization: `Bearer ${token}`, // keep as you have it
          "Content-Type": "application/json",
        },
      });
      if (response.data.success) {
        toast.success(response.data.message);
        fetchProfileData();
        setProfileData(response.data.data.user);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("Error marking leave:", error);
      toast.error("Failed to update leave status");
    }
  };

  const handleApproveLeave = async (leaveId, status) => {
    try {
      const response = await axios.patch(
        `${API}/leaves/${leaveId}/approve`,
        { status },
        {
          headers: {
            Authorization: `Bearer ${token}`, // keep as you have it
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data.success) {
        toast.success(response.data.message);
        fetchProfileData();
        setProfileData(response.data.data.user);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("Error approving leave:", error);
      toast.error("Failed to update leave status");
    }
  };

  const isCurrentMonth = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();

    return (
      d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()
    );
  };

  return (
    <div data-testid="profile-tab">
      <div className="flex items-center justify-between mb-6">
        <h2
          style={{
            fontSize: "1.5rem",
            fontWeight: "700",
          }}
        >
          {!user || currentUser.id === user.id
            ? "My Profile"
            : `${profileData.name}'s Profile`}
        </h2>

        {profileData.role != "admin" && (
          <button
            onClick={handleViewDetails} // define this function
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
          >
            View Details
          </button>
        )}
      </div>

      {/* User Info Card */}
      <div
        style={{
          background: "white",
          padding: "2rem",
          borderRadius: "1rem",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          marginBottom: "2rem",
        }}
        data-testid="user-info-card"
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "1.5rem",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "0.85rem",
                color: "#718096",
                fontWeight: "500",
                marginBottom: "0.25rem",
              }}
            >
              Full Name
            </div>
            <div
              style={{
                fontSize: "1.1rem",
                fontWeight: "600",
                color: "#1a202c",
              }}
              data-testid="profile-name"
            >
              {profileData.name}
            </div>
          </div>
          <div>
            <div
              style={{
                fontSize: "0.85rem",
                color: "#718096",
                fontWeight: "500",
                marginBottom: "0.25rem",
              }}
            >
              Email
            </div>
            <div
              style={{
                fontSize: "1.1rem",
                fontWeight: "600",
                color: "#1a202c",
              }}
              data-testid="profile-email"
            >
              {profileData.email}
            </div>
          </div>
          <div>
            <div
              style={{
                fontSize: "0.85rem",
                color: "#718096",
                fontWeight: "500",
                marginBottom: "0.25rem",
              }}
            >
              Role
            </div>
            <span
              className="status-badge"
              style={{
                background: "#667eea",
                color: "white",
                fontSize: "0.9rem",
                padding: "0.5rem 1rem",
              }}
              data-testid="profile-role"
            >
              {profileData.role}
            </span>
          </div>
          <div>
            <div
              style={{
                fontSize: "0.85rem",
                color: "#718096",
                fontWeight: "500",
                marginBottom: "0.25rem",
              }}
            >
              Joining Date
            </div>
            <div
              style={{
                fontSize: "1.1rem",
                fontWeight: "600",
                color: "#1a202c",
              }}
              data-testid="profile-joining-date"
            >
              {joiningDate}
            </div>
          </div>
        </div>
      </div>

      {/* Leave Statistics */}
      <div style={{ marginBottom: "2rem" }}>
        <h3
          style={{
            fontSize: "1.25rem",
            fontWeight: "700",
            marginBottom: "1rem",
          }}
        >
          Leave Statistics
        </h3>
        <div className="stats-grid">
          <div
            className="stat-card"
            style={{ borderLeftColor: "#48bb78" }}
            data-testid="earned-remaining-card"
          >
            <div className="stat-label">Total Earned Leaves</div>
            <div className="stat-value" style={{ color: "#48bb78" }}>
              {profileData.earned_leaves.toFixed(1)}
            </div>
          </div>
          <div
            className="stat-card"
            style={{ borderLeftColor: "#ed8936" }}
            data-testid="sick-leaves-card"
          >
            <div className="stat-label">Total Sick Leaves</div>
            <div className="stat-value" style={{ color: "#ed8936" }}>
              {profileData.sick_leaves.toFixed(1)}
            </div>
          </div>
          <div
            className="stat-card"
            style={{ borderLeftColor: "#4299e1" }}
            data-testid="casual-leaves-card"
          >
            <div className="stat-label">Total Causal Leave Taken</div>
            <div className="stat-value" style={{ color: "#4299e1" }}>
              {profileData.casual_leaves_taken.toFixed(1)}
            </div>
          </div>
          <div
            className="stat-card"
            style={{ borderLeftColor: "#667eea" }}
            data-testid="total-leaves-card"
          >
            <div className="stat-label">Total Sick Leaves Taken</div>
            <div className="stat-value" style={{ color: "#667eea" }}>
              {profileData.sick_leaves_taken.toFixed(1)}
            </div>
          </div>
        </div>
      </div>

      {/* Leave Summary */}
      <div style={{ marginBottom: "2rem" }}>
        <h3
          style={{
            fontSize: "1.25rem",
            fontWeight: "700",
            marginBottom: "1rem",
          }}
        >
          Leave Application Summary
        </h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "1rem",
          }}
        >
          <div
            style={{
              background: "#c6f6d5",
              padding: "1.5rem",
              borderRadius: "0.75rem",
              textAlign: "center",
            }}
            data-testid="approved-count-card"
          >
            <div
              style={{
                fontSize: "2.5rem",
                fontWeight: "700",
                color: "#22543d",
              }}
            >
              {summary.approved}
            </div>
            <div
              style={{
                fontSize: "0.9rem",
                color: "#22543d",
                fontWeight: "600",
              }}
            >
              Approved
            </div>
          </div>
          <div
            style={{
              background: "#fef5e7",
              padding: "1.5rem",
              borderRadius: "0.75rem",
              textAlign: "center",
            }}
            data-testid="pending-count-card"
          >
            <div
              style={{
                fontSize: "2.5rem",
                fontWeight: "700",
                color: "#d69e2e",
              }}
            >
              {summary.pending}
            </div>
            <div
              style={{
                fontSize: "0.9rem",
                color: "#d69e2e",
                fontWeight: "600",
              }}
            >
              Pending
            </div>
          </div>
          <div
            style={{
              background: "#fed7d7",
              padding: "1.5rem",
              borderRadius: "0.75rem",
              textAlign: "center",
            }}
            data-testid="rejected-count-card"
          >
            <div
              style={{
                fontSize: "2.5rem",
                fontWeight: "700",
                color: "#c53030",
              }}
            >
              {summary.rejected}
            </div>
            <div
              style={{
                fontSize: "0.9rem",
                color: "#c53030",
                fontWeight: "600",
              }}
            >
              Rejected
            </div>
          </div>
          <div
            style={{
              background: "#e6f2ff",
              padding: "1.5rem",
              borderRadius: "0.75rem",
              textAlign: "center",
            }}
            data-testid="total-days-card"
          >
            <div
              style={{
                fontSize: "2.5rem",
                fontWeight: "700",
                color: "#0066cc",
              }}
            >
              {summary.totalDaysApproved.toFixed(1)}
            </div>
            <div
              style={{
                fontSize: "0.9rem",
                color: "#0066cc",
                fontWeight: "600",
              }}
            >
              Days Approved
            </div>
          </div>
        </div>
      </div>

      {/* Leave History */}
      <div>
        <h3
          style={{
            fontSize: "1.25rem",
            fontWeight: "700",
            marginBottom: "1rem",
          }}
        >
          Complete Leave History
        </h3>
        {/* inside your component render */}
        {leaves.length > 0 ? (
          <div style={{ overflowX: "auto" }}>
            <table className="data-table" data-testid="profile-leaves-table">
              <thead>
                <tr>
                  <th>Leave Type</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Duration</th>
                  <th>Paid Days</th>
                  <th>Unpaid Days</th>
                  <th>Status</th>
                  <th>Reason</th>
                  <th>Applied On</th>
                  <th>Actions</th> {/* new header */}
                </tr>
              </thead>
              <tbody>
                {leaves.map((leave) => (
                  <tr
                    key={leave.id}
                    data-testid={`profile-leave-row-${leave.id}`}
                  >
                    <td style={{ textTransform: "capitalize" }}>
                      {leave.leave_type}
                    </td>
                    <td>
                      {new Date(leave.start_date).toLocaleDateString("en-IN")}
                    </td>
                    <td>
                      {new Date(leave.end_date).toLocaleDateString("en-IN")}
                    </td>
                    <td>
                      {leave.duration} {leave.duration === 1 ? "day" : "days"}
                    </td>
                    <td>
                      {leave.status === "approved" ? (
                        <>
                          {leave.duration - leave.unpaid_leaves}{" "}
                          {leave.duration - leave.unpaid_leaves === 1
                            ? "day"
                            : "days"}
                        </>
                      ) : (
                        "-"
                      )}
                    </td>

                    <td>
                      {leave.status === "approved" ? (
                        <>
                          {leave.unpaid_leaves}{" "}
                          {leave.unpaid_leaves < 1 ? "day" : "days"}
                        </>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      <span className={`status-badge status-${leave.status}`}>
                        {leave.status}
                      </span>
                    </td>
                    <td
                      style={{
                        maxWidth: "200px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {leave.reason}
                    </td>
                    <td>
                      {new Date(leave.created_at).toLocaleDateString("en-IN")}
                    </td>

                    {/* Actions column */}
                    <td>
                      {leave.status === "pending" &&
                      currentUser.email == profileData.email ? (
                        <div style={{ display: "flex", gap: 8 }}>
                          <button
                            type="button"
                            data-testid={`edit-leave-${leave.id}`}
                            onClick={() => onEditLeave(leave.id)}
                            style={{
                              padding: "6px 10px",
                              borderRadius: 6,
                              border: "1px solid #CBD5E0",
                              background: "white",
                              cursor: "pointer",
                            }}
                            aria-label={`Edit leave ${leave.id}`}
                          >
                            Edit
                          </button>

                          <button
                            type="button"
                            data-testid={`delete-leave-${leave.id}`}
                            onClick={() => {
                              if (
                                window.confirm(
                                  "Are you sure you want to delete this leave?",
                                )
                              ) {
                                deleteLeave(leave.id);
                              }
                            }}
                            style={{
                              padding: "6px 10px",
                              borderRadius: 6,
                              border: "1px solid #FEB2B2",
                              background: "white",
                              cursor: "pointer",
                            }}
                            aria-label={`Delete leave ${leave.id}`}
                          >
                            Delete
                          </button>
                        </div>
                      ) : leave.status === "approved" &&
                          currentUser.role === "admin"
                          // && isCurrentMonth(leave.start_date)
                          ? (
                        <div style={{ display: "flex", gap: 8 }}>
                          <button
                            type="button"
                            data-testid={`edit-leave-${leave.id}`}
                            onClick={() =>
                              handleApproveLeave(leave.id, "rejected")
                            }
                            style={{
                              padding: "6px 10px",
                              borderRadius: 6,
                              border: "1px solid #CBD5E0",
                              background: "white",
                              cursor: "pointer",
                            }}
                            aria-label={`Edit leave ${leave.id}`}
                          >
                            Revert Approval
                          </button>

                          <button
                            type="button"
                            data-testid={`delete-leave-${leave.id}`}
                            onClick={() => makeUnpaid(leave.id)}
                            style={{
                              padding: "6px 10px",
                              borderRadius: 6,
                              border: "1px solid #FEB2B2",
                              background: "white",
                              cursor: "pointer",
                            }}
                            aria-label={`Delete leave ${leave.id}`}
                          >
                            Mark Unpaid
                          </button>
                        </div>
                      ) : (
                        <span style={{ color: "#94A3B8", fontSize: 13 }}>
                          —
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state" data-testid="no-leave-history">
            <p>No leave history available</p>
            <small style={{ color: "#a0aec0" }}>
              Leave applications will appear here
            </small>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProfileTab;
